import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchStudentApplications } from 'api/applications'
import { fetchEssaySummary } from 'api/essaySummary'
import { fetchAppAssignments } from 'api/appAssignments'
import { essaySummaryStore } from 'pages/ApplicationsPage/model/essaySummaryStore'
import { appAssignmentsStore } from 'pages/ApplicationsPage/model/appAssignmentsStore'
import {
	APPLICATIONS_POLL_MS,
	ESSAY_SUMMARY_POLL_MS,
	APP_ASSIGNMENTS_POLL_MS,
	STARTUP_STAGGER_MS,
} from 'pages/ApplicationsPage/config/applicationsPolling'
import {
	APPLICATION_ROADMAP_STATUSES,
	normalizeApplicationStatusKey,
} from 'pages/ApplicationDetailsPage/utils/applicationStatusUi'
import { ROUTES } from 'shared/config/routes'
import { studentApplicationsStore } from '../model/studentApplicationsStore'

const KEY_TO_FIELD = {
	student: 'studentName',
	university: 'university',
	deadline: 'deadline',
}

const parseDate = value => {
	if (value == null) return null
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? null : d.getTime()
}

const sortRows = (rows, key, dir) => {
	if (!key) return rows
	const sign = dir === 'desc' ? -1 : 1
	const field = KEY_TO_FIELD[key] || key
	return [...rows].sort((a, b) => {
		const av = a?.[field] ?? a?.[key]
		const bv = b?.[field] ?? b?.[key]
		if (av == null && bv == null) return 0
		if (av == null) return 1
		if (bv == null) return -1
		if (key === 'deadline') {
			const at = parseDate(av)
			const bt = parseDate(bv)
			if (at != null && bt != null) return (at - bt) * sign
		}
		return (
			String(av).localeCompare(String(bv), undefined, {
				sensitivity: 'base',
			}) * sign
		)
	})
}

export const useStudentApplicationsTab = (studentId, isActive = false) => {
	const navigate = useNavigate()
	const store = studentApplicationsStore

	const prevStudentRef = useRef(undefined)
	if (prevStudentRef.current !== studentId) {
		prevStudentRef.current = studentId
		store.ensureStudent(studentId)
	}

	const [error, setError] = useState(null)
	const [sortKey, setSortKey] = useState(null)
	const [sortDir, setSortDir] = useState('asc')
	const [selectedStages, setSelectedStages] = useState([])

	const appsInFlight = useRef(false)
	const essayInFlight = useRef(false)
	const assignInFlight = useRef(false)

	const loadApplications = useCallback(async () => {
		if (!studentId || appsInFlight.current) return
		appsInFlight.current = true
		try {
			if (!store.hasDataFor(studentId)) store.setLoading(studentId)
			const data = await fetchStudentApplications(studentId)
			store.setApplications(studentId, data?.applications)
			setError(null)
		} catch (e) {
			setError(e?.message || 'Failed to load applications')
			store.setError(studentId)
		} finally {
			appsInFlight.current = false
		}
	}, [studentId, store])

	const loadEssaySummary = useCallback(async () => {
		if (essayInFlight.current) return
		essayInFlight.current = true
		try {
			if (essaySummaryStore.loadStatus === 'idle') {
				essaySummaryStore.setLoading()
			}
			const payload = await fetchEssaySummary()
			essaySummaryStore.setPayload(payload)
		} catch (err) {
			console.error('Failed to load essay summary:', err)
			essaySummaryStore.setError()
		} finally {
			essayInFlight.current = false
		}
	}, [])

	const loadAppAssignments = useCallback(async () => {
		if (assignInFlight.current) return
		assignInFlight.current = true
		try {
			if (appAssignmentsStore.loadStatus === 'idle') {
				appAssignmentsStore.setLoading()
			}
			const payload = await fetchAppAssignments()
			appAssignmentsStore.setPayload(payload)
		} catch (err) {
			console.error('Failed to load app assignments:', err)
			appAssignmentsStore.setError()
		} finally {
			assignInFlight.current = false
		}
	}, [])

	const loadApplicationsRef = useRef(loadApplications)
	const loadEssaySummaryRef = useRef(loadEssaySummary)
	const loadAppAssignmentsRef = useRef(loadAppAssignments)
	loadApplicationsRef.current = loadApplications
	loadEssaySummaryRef.current = loadEssaySummary
	loadAppAssignmentsRef.current = loadAppAssignments

	useEffect(() => {
		if (!studentId || !isActive) return undefined

		loadApplicationsRef.current()

		const essayTimer = window.setTimeout(() => {
			loadEssaySummaryRef.current()
		}, STARTUP_STAGGER_MS)

		const assignTimer = window.setTimeout(() => {
			loadAppAssignmentsRef.current()
		}, STARTUP_STAGGER_MS * 2)

		const appsId = window.setInterval(() => {
			loadApplicationsRef.current()
		}, APPLICATIONS_POLL_MS)
		const essayId = window.setInterval(() => {
			loadEssaySummaryRef.current()
		}, ESSAY_SUMMARY_POLL_MS)
		const assignId = window.setInterval(() => {
			loadAppAssignmentsRef.current()
		}, APP_ASSIGNMENTS_POLL_MS)

		return () => {
			window.clearTimeout(essayTimer)
			window.clearTimeout(assignTimer)
			window.clearInterval(appsId)
			window.clearInterval(essayId)
			window.clearInterval(assignId)
		}
	}, [studentId, isActive])

	const onSortChange = useCallback(({ opt }) => {
		if (!opt?.key || !opt?.dir) return
		setSortKey(opt.key)
		setSortDir(opt.dir)
	}, [])

	const onRowNavigate = useCallback(
		row => {
			const id = String(row?.id || '').trim()
			if (!id) return
			navigate(`${ROUTES.APPLICATIONS}/${encodeURIComponent(id)}`, {
				state: { backTo: { type: 'studentProfile', studentId } },
			})
		},
		[navigate, studentId],
	)

	const rows = store.applications
	const isLoading =
		store.loadStatus !== 'success' && store.loadStatus !== 'error'

	const sortedRows = useMemo(
		() => sortRows(rows, sortKey, sortDir),
		[rows, sortKey, sortDir],
	)

	const countsByStatus = useMemo(() => {
		const map = Object.fromEntries(
			APPLICATION_ROADMAP_STATUSES.map(item => [item.id, 0]),
		)
		rows.forEach(app => {
			const key = normalizeApplicationStatusKey(app?.applicationStatus)
			if (key in map) map[key] += 1
		})
		return map
	}, [rows])

	const filteredRows = useMemo(() => {
		if (selectedStages.length === 0) return sortedRows
		return sortedRows.filter(app =>
			selectedStages.includes(
				normalizeApplicationStatusKey(app?.applicationStatus),
			),
		)
	}, [sortedRows, selectedStages])

	const toggleStatus = useCallback(statusId => {
		const key = String(statusId || '').trim()
		if (!key) return
		setSelectedStages(prev =>
			prev.includes(key) ? prev.filter(v => v !== key) : [...prev, key],
		)
	}, [])

	const clearStatusFilters = useCallback(() => setSelectedStages([]), [])

	const isStatusActive = useCallback(
		statusId => selectedStages.includes(statusId),
		[selectedStages],
	)

	const roadmap = useMemo(
		() => ({
			statuses: APPLICATION_ROADMAP_STATUSES,
			countsByStatus,
			totalCount: rows.length,
			isAllActive: selectedStages.length === 0,
			toggleStatus,
			clearStatusFilters,
			isStatusActive,
		}),
		[
			countsByStatus,
			rows.length,
			selectedStages.length,
			toggleStatus,
			clearStatusFilters,
			isStatusActive,
		],
	)

	return {
		isLoading,
		error,
		rows: filteredRows,
		sortKey,
		sortDir,
		onSortChange,
		onRowNavigate,
		roadmap,
	}
}

export default useStudentApplicationsTab
